import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Paperclip } from 'lucide-react';
import { createBill } from '../../../../services/finance_services/billService';
import { uploadBillAttachment } from '../../../../services/finance_attachment_services/billAttachmentService';
import ReportForm from '../../../../components/ReportForm';
import Message from '../../../../components/Message';
import useApi from '../../../../hooks/useApi';

const AddBillPage = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [billNumber, setBillNumber] = useState('');
  const [dateIssued, setDateIssued] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [vendor, setVendor] = useState('');
  // const [status, setStatus] = useState('pending');
  const [amountPaid, setAmountPaid] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [paymentReference, setPaymentReference] = useState('');

  const [files, setFiles] = useState([]);
  const { loading, error, successMessage, callApi: createBillApi, setError, setSuccessMessage } = useApi(createBill, {showSuccessToast: true, msg:'New bill report created'});

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const billData = {
      title,
      description,
      amount,
      bill_number: billNumber,
      date_issued: dateIssued,
      due_date: dueDate,
      vendor,
      // status,
      amount_paid: amountPaid,
      payment_date: paymentDate || null,
      payment_reference: paymentReference,
    };
    try {
      const response = await createBillApi(billData);
      if (response && response.id && files.length > 0) {
        await Promise.all(
          files.map(file => {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('title', file.name);
            return uploadBillAttachment(response.id, formData);
          })
        );
      }
      setSuccessMessage('Bill created successfully!');
      setTimeout(() => {
        navigate('/dashboard/bills');
      }, 1500);
    } catch (err) {
      setError(err.message);
    }
  };

  const formFields = [
    { label: 'Title', type: 'text', value: title, onChange: e => setTitle(e.target.value), required: true },
    { label: 'Description', type: 'textarea', value: description, onChange: e => setDescription(e.target.value) },
    { label: 'Amount', type: 'number', value: amount, onChange: e => setAmount(e.target.value), required: true },
    { label: 'Bill Number', type: 'text', value: billNumber, onChange: e => setBillNumber(e.target.value) },
    { label: 'Date Issued', type: 'date', value: dateIssued, onChange: e => setDateIssued(e.target.value), required: true },
    { label: 'Due Date', type: 'date', value: dueDate, onChange: e => setDueDate(e.target.value), required: true },
    { label: 'Vendor', type: 'text', value: vendor, onChange: e => setVendor(e.target.value) },
    // { label: 'Status', type: 'select', value: status, onChange: e => setStatus(e.target.value), options: [
    //     { value: 'pending', label: 'Pending' },
    //     { value: 'paid', label: 'Paid' },
    //     { value: 'overdue', label: 'Overdue' },
    //     { value: 'partially_paid', label: 'Partially Paid' },
    //     { value: 'cancelled', label: 'Cancelled' },
    //   ]
    // },
    { label: 'Amount Paid', type: 'number', value: amountPaid, onChange: e => setAmountPaid(e.target.value) },
    { label: 'Payment Date', type: 'date', value: paymentDate, onChange: e => setPaymentDate(e.target.value) },
    { label: 'Payment Reference', type: 'text', value: paymentReference, onChange: e => setPaymentReference(e.target.value) },

  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/dashboard/bills')}
          className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Bills
        </button>
        <h1 className="text-2xl font-bold text-gray-900">Add Bill</h1>
      </div>

      {/* Display Messages */}
      <Message type="error" message={error} />
      <Message type="success" message={successMessage} />
      
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <ReportForm
        title="Add Bill"
        fields={formFields}
        onSubmit={handleSubmit}
        onCancel={() => navigate('/dashboard/bills')}
        loading={loading}
        submitButtonText="Create Bill"
      >
        {/* New Attachments */}
        <div>
          <label htmlFor="attachments" className="block text-sm font-medium text-gray-700">
            Add Attachments (Receipts, Invoices, etc.)
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
            <div className="space-y-1 text-center">
              <Paperclip className="mx-auto h-12 w-12 text-gray-400" />
              <div className="flex text-sm text-gray-600">
                <label
                  htmlFor="file-upload"
                  className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none"
                >
                  <span>Upload files</span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    multiple
                    onChange={handleFileChange}
                  />
                </label>
                <p className="pl-1">or drag and drop</p>
              </div>
              <p className="text-xs text-gray-500">
                PDF, JPG, PNG up to 10MB
              </p>
            </div>
            {files.length > 0 && (
              <div className="mt-2">
                <p className="text-sm text-gray-500">Files to upload:</p>
                <ul className="mt-1 text-sm text-gray-700">
                  {Array.from(files).map((file, index) => (
                    <li key={index} className="flex items-center">
                      <Paperclip className="h-4 w-4 mr-2 text-gray-500" />
                      {file.name} ({Math.round(file.size / 1024)} KB)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </ReportForm>
      </div>
    </div>
  );
};

export default AddBillPage;

